import dayjs from "dayjs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ApiErrorResponse } from "../../../models/ApiResponse";
import Invoice from "../../../models/Invoice";
import {
	useConfirmInvoiceExported,
	useReleaseInvoiceTimeReports,
	useResolveInvoiceAttachment,
} from "../../../networking/invoice.hooks";
import { getInvoiceExportIssues, InvoiceExportIssue } from "../../../utils/invoiceExport.utils";
import { toast } from "../../../utils/toast";
import ButtonNew from "../../Core/Button/ButtonNew";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import InvoiceExportStatusChip from "./InvoiceExportStatusChip";

const actionButtonClasses =
	"inline-flex h-7 items-center rounded-md border border-solid border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";

interface Props {
	invoice: Invoice;
	/** Shown in the dialogs so the admin knows which invoice to look up in Visma. */
	customerName: string;
	/** Called after an action succeeded, so the owner can refetch the batch. */
	onChanged?: () => unknown;
}

function formatResolvedAt(at?: string) {
	return at ? dayjs(at).format("YYYY-MM-DD HH:mm") : "";
}

/**
 * Per-invoice export outcome of a processed (Visma.net) batch: a chip with the
 * reason and, for open items, the admin's follow-up actions. Renders nothing
 * for an invoice without export problems.
 */
const InvoiceExportStatus: React.FC<Props> = ({ invoice, customerName, onChanged }) => {
	const { t } = useTranslation();
	const issues = getInvoiceExportIssues(invoice);

	const [dialog, setDialog] = useState<"release" | "confirm">();
	const [invoiceNumber, setInvoiceNumber] = useState("");
	const [showRequired, setShowRequired] = useState(false);

	const release = useReleaseInvoiceTimeReports();
	const confirmExported = useConfirmInvoiceExported();
	const resolveAttachment = useResolveInvoiceAttachment();

	if (!issues.length) return null;

	async function run(action: () => Promise<unknown>, successKey: string) {
		try {
			await action();
			toast.success(t(successKey as any));
			setDialog(undefined);
			await onChanged?.();
		} catch (e) {
			toast.error(t("invoiceBatch.exportStatus.actionFailed"), {
				description: (e as ApiErrorResponse)?.error?.detail || undefined,
			});
		}
	}

	function openConfirmDialog() {
		setInvoiceNumber("");
		setShowRequired(false);
		setDialog("confirm");
	}

	function handleConfirmSubmit(e: React.FormEvent) {
		e.preventDefault();
		const trimmed = invoiceNumber.trim();

		if (!trimmed) {
			setShowRequired(true);
			return;
		}

		run(
			() => confirmExported.mutateAsync({ invoiceId: invoice.id, invoiceNumber: trimmed }),
			"invoiceBatch.exportStatus.success.confirmed"
		);
	}

	const renderIssue = (issue: InvoiceExportIssue) => {
		const isOpen = !issue.resolved;

		return (
			<div key={issue.kind} className="flex flex-col gap-1">
				<div className="flex flex-wrap items-center gap-2">
					<InvoiceExportStatusChip issue={issue} />
					{issue.resolved && (
						<span className="text-xs text-gray-500">
							{t("invoiceBatch.exportStatus.resolvedAt", {
								date: formatResolvedAt(issue.resolved.at),
							})}
							{issue.resolved.outcome === "CONFIRMED_EXPORTED" && invoice.invoiceNumber && (
								<>
									{" · "}
									{t("invoiceBatch.exportStatus.invoiceNumber", {
										invoiceNumber: invoice.invoiceNumber,
									})}
								</>
							)}
						</span>
					)}
				</div>

				{issue.reason && (
					<p
						className={cn(
							"m-0 whitespace-normal break-words text-xs",
							isOpen ? "text-gray-700" : "text-gray-400"
						)}
					>
						{issue.reason}
					</p>
				)}

				{isOpen && issue.kind === "HELD" && (
					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							className={actionButtonClasses}
							onClick={() => setDialog("release")}
						>
							{t("invoiceBatch.exportStatus.notInVisma")}
						</button>
						<button type="button" className={actionButtonClasses} onClick={openConfirmDialog}>
							{t("invoiceBatch.exportStatus.inVisma")}
						</button>
					</div>
				)}

				{isOpen && issue.kind === "ATTACHMENT_MISSING" && (
					<>
						<p className="m-0 whitespace-normal text-xs text-gray-500">
							{t("invoiceBatch.exportStatus.attachmentHelp")}
						</p>
						<div>
							<button
								type="button"
								className={actionButtonClasses}
								disabled={resolveAttachment.isLoading}
								onClick={() =>
									run(
										() => resolveAttachment.mutateAsync(invoice.id),
										"invoiceBatch.exportStatus.success.attachmentResolved"
									)
								}
							>
								{t("invoiceBatch.exportStatus.markResolved")}
							</button>
						</div>
					</>
				)}
			</div>
		);
	};

	return (
		<div className="flex flex-col gap-2">
			{issues.map(renderIssue)}

			<Dialog open={dialog === "release"} onOpenChange={(open) => !open && setDialog(undefined)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("invoiceBatch.exportStatus.releaseDialog.title")}</DialogTitle>
						<DialogDescription>
							{t("invoiceBatch.exportStatus.releaseDialog.description", { customer: customerName })}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<ButtonNew
							variant="neutral"
							className="border-solid"
							onClick={() => setDialog(undefined)}
						>
							{t("CANCEL")}
						</ButtonNew>
						<ButtonNew
							variant="primary"
							disabled={release.isLoading}
							onClick={() =>
								run(
									() => release.mutateAsync(invoice.id),
									"invoiceBatch.exportStatus.success.released"
								)
							}
						>
							{t("invoiceBatch.exportStatus.releaseDialog.confirm")}
						</ButtonNew>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={dialog === "confirm"} onOpenChange={(open) => !open && setDialog(undefined)}>
				<DialogContent>
					<form onSubmit={handleConfirmSubmit} className="flex flex-col gap-4" noValidate>
						<DialogHeader>
							<DialogTitle>{t("invoiceBatch.exportStatus.confirmDialog.title")}</DialogTitle>
							<DialogDescription>
								{t("invoiceBatch.exportStatus.confirmDialog.description", {
									customer: customerName,
								})}
							</DialogDescription>
						</DialogHeader>

						<label className="flex flex-col gap-1.5 text-sm font-medium text-gray-700">
							{t("invoiceBatch.exportStatus.confirmDialog.invoiceNumber")}
							<Input
								autoFocus
								required
								value={invoiceNumber}
								aria-invalid={showRequired && !invoiceNumber.trim()}
								onChange={(e) => setInvoiceNumber(e.target.value)}
								className={cn(
									"border-solid font-normal",
									showRequired && !invoiceNumber.trim() && "border-red-500"
								)}
							/>
							{showRequired && !invoiceNumber.trim() && (
								<span role="alert" className="text-xs font-normal text-red-600">
									{t("invoiceBatch.exportStatus.confirmDialog.required")}
								</span>
							)}
						</label>

						<DialogFooter>
							<ButtonNew
								variant="neutral"
								className="border-solid"
								onClick={() => setDialog(undefined)}
							>
								{t("CANCEL")}
							</ButtonNew>
							<ButtonNew type="submit" variant="primary" disabled={confirmExported.isLoading}>
								{t("invoiceBatch.exportStatus.confirmDialog.confirm")}
							</ButtonNew>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default InvoiceExportStatus;
